import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import "@/lib/api"; // Initialize axios interceptors early
import axios from "axios";
import { motion } from "framer-motion";
import {
  Kanban, Microphone, Lightning, LinkedinLogo, Image, Coffee,
  Binoculars, EnvelopeSimple, FilmStrip, List, Sparkle, FolderOpen, VideoCamera,
  DotsSixVertical, SignOut, User, Coins, DiscordLogo, GraduationCap, Gift, Rocket, Brain, FilmReel, ChartBar, Question, Robot, FilmSlate, GridFour, PaintBrush, Lock,
} from "@phosphor-icons/react";
import { LOGO_URL, API, getAuthHeaders } from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Page components
import ContentPipeline from "@/pages/ContentPipeline";
import ScriptStudio from "@/pages/ScriptStudio";
import HookCaption from "@/pages/HookCaption";
import LinkedInEngine from "@/pages/LinkedInEngine";
import AITeaTuesday from "@/pages/AITeaTuesday";
import ResearchLibrary from "@/pages/ResearchLibrary";
import ContentVault from "@/pages/ContentVault";
import AutoCreate from "@/pages/AutoCreate";
import AITwinGallery from "@/pages/AITwinGallery";
import CloneMyVoice from "@/pages/CloneMyVoice";
import PaymentSuccess from "@/pages/PaymentSuccess";
import NewsletterIntel from "@/pages/NewsletterIntel";
import LifestyleVideo from "@/pages/LifestyleVideo";
import AllMyVideos from "@/pages/AllMyVideos";
import AccountSettings from "@/pages/AccountSettings";
import ReferralPage from "@/pages/ReferralPage";
import BulkCreate from "@/pages/BulkCreate";
import CarouselStudio from "@/pages/CarouselStudio";
import DesignStudio from "@/pages/DesignStudio";
import AutoAgent from "@/pages/AutoAgent";
import LongFormStudio from "@/pages/LongFormStudio";
import BrandKnowledgeBase from "@/pages/BrandKnowledgeBase";
import SeriesStudio from "@/pages/SeriesStudio";
import AdminDashboard from "@/pages/AdminDashboard";
import HelpPricing from "@/pages/HelpPricing";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingPage from "@/pages/OnboardingPage";

const DEFAULT_NAV_ITEMS = [
  { id: "brand-kb", label: "Brand Knowledge", icon: Brain },
  { id: "ai-gallery", label: "AI Twin Gallery", icon: Image },
  { id: "pipeline", label: "Content Pipeline", icon: Kanban },
  { id: "auto-create", label: "Auto Create", icon: Sparkle },
  { id: "bulk-create", label: "Bulk Create", icon: Rocket },
  { id: "carousel", label: "Carousel Studio", icon: GridFour },
  { id: "design", label: "Design Studio", icon: PaintBrush },
  { id: "auto-agent", label: "Auto Agent", icon: Robot, feature: "auto_agent" },
  { id: "long-form", label: "Long-Form Studio", icon: FilmSlate, feature: "long_form" },
  { id: "lifestyle", label: "Lifestyle Video", icon: FilmStrip },
  { id: "my-videos", label: "All My Videos", icon: VideoCamera },
  { id: "linkedin", label: "LinkedIn Engine", icon: LinkedinLogo },
  { id: "script", label: "Script Studio", icon: Microphone },
  { id: "clone-voice", label: "Clone My Voice", icon: Microphone, feature: "clone_my_voice" },
  { id: "hooks", label: "Hook + Caption", icon: Lightning, feature: "hook_captions" },
  { id: "series", label: "Series Studio", icon: FilmReel, feature: "series_studio" },
  { id: "vault", label: "Content Vault", icon: FolderOpen },
  { id: "ai-tea", label: "Industry News", icon: Coffee },
  { id: "research", label: "Research Library", icon: Binoculars },
  { id: "newsletter", label: "Newsletter Intel", icon: EnvelopeSimple },
  { id: "account", label: "Credits & Keys", icon: Coins },
];

import GuidedTour from "@/components/GuidedTour";
import NotificationBell from "@/components/NotificationBell";
import CreditsPill from "@/components/CreditsPill";
import LowCreditBanner from "@/components/LowCreditBanner";
import UpgradeModal from "@/components/UpgradeModal";
import AnnouncementBanner, { AnnouncementEditorModal } from "@/components/AnnouncementBanner";
import PricingPage from "@/pages/PricingPage";
import PlanComparison from "@/pages/PlanComparison";
import GoRedirect from "@/pages/GoRedirect";
import WebinarPage from "@/pages/WebinarPage";
import ChatWidget from "@/components/ChatWidget";
import GlobalRenderTracker from "@/components/GlobalRenderTracker";
import { Toaster, toast } from "@/components/ui/sonner";

function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("brand-kb");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);
  // Feature-gate state: /api/plans/me returns unlocked_features[] + feature_gates{}
  const [unlockedFeatures, setUnlockedFeatures] = useState([]);
  const [featureGates, setFeatureGates] = useState({});
  const [upgradeModal, setUpgradeModal] = useState(null); // {feature, tier_required, feature_display}

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/plans/me`, { headers: getAuthHeaders() });
        if (r.ok) {
          const d = await r.json();
          setUnlockedFeatures(d.unlocked_features || []);
          setFeatureGates(d.feature_gates || {});
        }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const hasFeature = (fkey) => !fkey || unlockedFeatures.includes(fkey);
  const TIER_DISPLAY = {
    starter: "Starter ($39/mo)", creator: "Creator ($79/mo)",
    pro: "Pro ($159/mo)", studio: "Studio ($359/mo)",
    enterprise: "Enterprise ($999/mo)",
  };
  const [navItems, setNavItems] = useState(() => {
    try {
      const saved = localStorage.getItem("nav_order");
      if (saved) {
        const order = JSON.parse(saved);
        return order.map((id) => DEFAULT_NAV_ITEMS.find((n) => n.id === id)).filter(Boolean);
      }
    } catch {}
    return DEFAULT_NAV_ITEMS;
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showAnnouncementEditor, setShowAnnouncementEditor] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [creditsRefreshKey, setCreditsRefreshKey] = useState(0);

  // Handle Stripe success return (?payment=success&session_id=XXX) + cancel
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");
    const type = params.get("type");
    if (!payment) return;

    // Clean the URL regardless so refresh doesn't re-trigger
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);

    if (payment === "success" && sessionId) {
      const endpoint = type === "subscription"
        ? `${API}/subscription/confirm/${sessionId}`
        : `${API}/credits/status/${sessionId}`;
      (async () => {
        for (let i = 0; i < 6; i++) {
          try {
            const { data } = await axios.get(endpoint, {
              headers: getAuthHeaders(),
              withCredentials: true,
            });
            if (data.status === "paid") {
              toast.success(
                type === "subscription"
                  ? `Subscription activated! +${data.credits_added || 0} credits`
                  : `Payment successful! +${data.credits_added || 0} credits`
              );
              await refreshUser();
              setCreditsRefreshKey((k) => k + 1);
              return;
            }
            if (data.status === "expired") {
              toast.error("Checkout expired. Please try again.");
              return;
            }
          } catch (_) {}
          await new Promise((r) => setTimeout(r, 1500));
        }
        toast("Payment is still processing. Credits will arrive shortly.");
      })();
    } else if (payment === "cancelled") {
      toast("Checkout cancelled.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for global 402 → open upgrade modal
  // EXCEPT: admin users (credits === -1) get unlimited everything — they
  // should NEVER see the upgrade modal, regardless of any 402 that bubbles up.
  useEffect(() => {
    const handler = (e) => {
      if (user?.credits === -1 || user?.role === "admin") {
        // Surface the actual error but skip the upsell.
        if (e.detail?.message) toast.error(e.detail.message);
        return;
      }
      const reason = e.detail?.reason || "out_of_credits";
      setUpgradeReason(reason);
      setUpgradeOpen(true);
      if (e.detail?.message) toast.error(e.detail.message);
    };
    window.addEventListener("open-upgrade-modal", handler);
    return () => window.removeEventListener("open-upgrade-modal", handler);
  }, [user?.credits, user?.role]);

  const openUpgrade = (reason = null) => {
    setUpgradeReason(reason);
    setUpgradeOpen(true);
  };

  useEffect(() => {
    // Auto-start tour for new users
    const tourSeen = localStorage.getItem("tour_completed");
    if (!tourSeen) {
      setTimeout(() => setShowWelcome(true), 1200);
    }
    // Periodic credit refresh
    const creditPoll = setInterval(() => refreshUser(), 30000);
    return () => clearInterval(creditPoll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavClick = (id, feature) => {
    if (feature && !hasFeature(feature)) {
      // Show upgrade modal instead of navigating
      const item = DEFAULT_NAV_ITEMS.find((n) => n.id === id);
      const required = featureGates[feature] || "starter";
      setUpgradeModal({
        feature,
        tier_required: required,
        feature_display: item?.label || feature,
        tier_display: TIER_DISPLAY[required] || required,
      });
      return;
    }
    setActiveSection(id);
    setSidebarOpen(false);
  };
  const onNavDragStart = (idx) => setDragIdx(idx);
  const onNavDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const onNavDragEnd = () => {
    if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
      const updated = [...navItems];
      const [moved] = updated.splice(dragIdx, 1);
      updated.splice(dragOverIdx, 0, moved);
      setNavItems(updated);
      localStorage.setItem("nav_order", JSON.stringify(updated.map((n) => n.id)));
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const renderSection = () => {
    switch (activeSection) {
      case "auto-create": return <AutoCreate />;
      case "vault": return <ContentVault />;
      case "pipeline": return <ContentPipeline />;
      case "script": return <ScriptStudio />;
      case "clone-voice": return <CloneMyVoice />;
      case "hooks": return <HookCaption />;
      case "linkedin": return <LinkedInEngine />;
      case "ai-gallery": return <AITwinGallery />;
      case "ai-tea": return <AITeaTuesday />;
      case "research": return <ResearchLibrary />;
      case "newsletter": return <NewsletterIntel />;
      case "lifestyle": return <LifestyleVideo />;
      case "my-videos": return <AllMyVideos />;
      case "account": return <AccountSettings />;
      case "referrals": return <ReferralPage />;
      case "bulk-create": return <BulkCreate />;
      case "carousel": return <CarouselStudio />;
      case "design": return <DesignStudio />;
      case "auto-agent": return <AutoAgent />;
      case "long-form": return <LongFormStudio />;
      case "brand-kb": return <BrandKnowledgeBase />;
      case "series": return <SeriesStudio />;
      case "admin": return <AdminDashboard />;
      case "help": return <HelpPricing />;
      case "pricing": return <PlanComparison inline={true} />;
      default: return <AutoCreate />;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="dashboard">
      {/* Global scrolling announcement (only renders if admin has enabled it) */}
      <AnnouncementBanner
        user={user}
        onAdminEdit={() => setShowAnnouncementEditor(true)}
      />
      <AnnouncementEditorModal
        open={showAnnouncementEditor}
        onClose={() => setShowAnnouncementEditor(false)}
      />
      <div className="flex flex-1 min-h-0">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`fixed inset-y-0 left-0 z-40 bg-white border-r border-zinc-200 transition-transform duration-300 w-64 lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center justify-center border-b border-zinc-100 px-4">
          <img src={LOGO_URL} alt="The Branding Bar" className="h-10 object-contain" />
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 14rem)" }}>
          {navItems.map((item, idx) => (
            <button
              key={item.id}
              draggable
              onDragStart={() => onNavDragStart(idx)}
              onDragOver={(e) => onNavDragOver(e, idx)}
              onDragEnd={onNavDragEnd}
              onClick={() => handleNavClick(item.id, item.feature)}
              data-testid={`nav-${item.id}`}
              className={`w-full sidebar-link group ${activeSection === item.id ? "sidebar-link-active" : ""} ${dragOverIdx === idx ? "border-t-2 border-[#E91E8C]" : ""} ${dragIdx === idx ? "opacity-40" : ""} ${item.feature && !hasFeature(item.feature) ? "opacity-60" : ""}`}
            >
              <item.icon size={20} weight={activeSection === item.id ? "duotone" : "regular"} />
              <span className="flex items-center gap-1.5">
                {item.label}
                {item.feature && !hasFeature(item.feature) && (
                  <Lock size={11} weight="fill" className="text-zinc-400" data-testid={`nav-lock-${item.id}`} />
                )}
              </span>
              <DotsSixVertical size={14} className="ml-auto text-zinc-300 opacity-0 group-hover:opacity-100" />
            </button>
          ))}
          {/* Community Links */}
          <div className="pt-3 mt-3 border-t border-zinc-100 space-y-1">
            <a href="https://www.skool.com/the-branding-bar-1662/about?ref=91ffe0d4be8342c79aa821533a969b33" target="_blank" rel="noopener noreferrer" className="w-full sidebar-link" data-testid="sidebar-skool">
              <GraduationCap size={20} className="text-[#E91E8C]" />
              <span>Skool Community</span>
            </a>
            <a href="https://discord.gg/BmwfRzykqX" target="_blank" rel="noopener noreferrer" className="w-full sidebar-link" data-testid="sidebar-discord">
              <DiscordLogo size={20} className="text-[#5865F2]" />
              <span>Discord Support</span>
            </a>
            <button onClick={() => handleNavClick("referrals")} data-testid="nav-referrals" className={`w-full sidebar-link ${activeSection === "referrals" ? "sidebar-link-active" : ""}`}>
              <Gift size={20} weight={activeSection === "referrals" ? "duotone" : "regular"} className="text-amber-500" />
              <span>Refer & Earn</span>
            </button>
            {user?.role === "admin" && (
              <button onClick={() => handleNavClick("admin")} data-testid="nav-admin" className={`w-full sidebar-link ${activeSection === "admin" ? "sidebar-link-active" : ""}`}>
                <ChartBar size={20} weight={activeSection === "admin" ? "duotone" : "regular"} className="text-[#E91E8C]" />
                <span>Admin Dashboard</span>
              </button>
            )}
            <button onClick={() => handleNavClick("help")} data-testid="nav-help" className={`w-full sidebar-link ${activeSection === "help" ? "sidebar-link-active" : ""}`}>
              <Question size={20} weight={activeSection === "help" ? "duotone" : "regular"} className="text-sky-500" />
              <span>Help & Pricing</span>
            </button>
            <button
              onClick={() => setRunTour(true)}
              data-testid="take-tour-btn"
              className="w-full sidebar-link text-zinc-400 hover:text-[#E91E8C]"
            >
              <Sparkle size={20} />
              <span>Take a Tour</span>
            </button>
          </div>
        </nav>
        {user && (
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-zinc-100 bg-white">
            <div className="flex items-center gap-3 px-3 py-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <User size={20} className="text-zinc-400" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 truncate">{user.name}</p>
                <p className="text-xs text-zinc-500 truncate">
                  {user.plan === "admin" || user.credits === -1
                    ? "Admin · Unlimited"
                    : user.subscription_tier && user.subscription_tier !== "free"
                      ? user.subscription_tier.charAt(0).toUpperCase() + user.subscription_tier.slice(1)
                      : "Free"}
                </p>
              </div>
              <button onClick={handleLogout} className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600" data-testid="logout-btn">
                <SignOut size={18} />
              </button>
            </div>
          </div>
        )}
      </aside>
      <main className="flex-1 min-h-screen min-w-0">
        <header className="h-14 sm:h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-zinc-100" data-testid="toggle-sidebar">
              <List size={22} />
            </button>
            <h1 className="text-base sm:text-lg font-bold text-zinc-900 truncate">
              {navItems.find((n) => n.id === activeSection)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs sm:text-sm text-zinc-500 hidden sm:block">
              {user?.brand_name || "The Branding Bar"} Dashboard
            </span>
            <CreditsPill
              refreshKey={creditsRefreshKey}
              onUpgrade={() => handleNavClick("pricing")}
            />
            <NotificationBell onNavigate={handleNavClick} />
          </div>
        </header>
        <div className="p-4 sm:p-6">
          <LowCreditBanner onUpgrade={() => handleNavClick("pricing")} />
          <motion.div key={activeSection} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            {renderSection()}
          </motion.div>
        </div>
      </main>

      <UpgradeModal
        open={upgradeOpen}
        reason={upgradeReason}
        onClose={() => setUpgradeOpen(false)}
        onViewPricing={() => {
          setUpgradeOpen(false);
          handleNavClick("pricing");
        }}
      />

      {/* In-app support chat — floating pink bubble bottom-right */}
      <ChatWidget />

      {/* Persistent cross-section render tracker */}
      <GlobalRenderTracker onNavigate={handleNavClick} />

      {/* Welcome Modal for new users. Click outside / X also dismisses it so
          users who don't want the tour aren't trapped. */}
      {showWelcome && (
        <div
          className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center px-4"
          onClick={() => { setShowWelcome(false); localStorage.setItem("tour_completed", "true"); }}
          data-testid="welcome-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setShowWelcome(false); localStorage.setItem("tour_completed", "true"); }}
              className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-700 text-xl leading-none w-7 h-7 rounded-full hover:bg-zinc-100 flex items-center justify-center"
              data-testid="welcome-close-btn"
              aria-label="Close"
            >
              ×
            </button>
            <div className="w-14 h-14 rounded-full bg-[#E91E8C]/10 flex items-center justify-center mx-auto mb-4">
              <Sparkle size={28} weight="fill" className="text-[#E91E8C]" />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-2">Welcome to The Branding Bar!</h3>
            <p className="text-sm text-zinc-500 mb-6">Take a 2-minute tour that walks through every menu item and explains — in plain English — what each one does. You can skip anytime.</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { setShowWelcome(false); setRunTour(true); }}
                className="w-full py-3 bg-[#E91E8C] text-white font-semibold rounded-xl hover:bg-[#d4177d] transition-all"
                data-testid="start-tour-btn"
              >
                Take the tour
              </button>
              <button
                onClick={() => { setShowWelcome(false); localStorage.setItem("tour_completed", "true"); }}
                className="w-full py-2.5 text-zinc-500 text-sm hover:text-zinc-700 transition-all"
                data-testid="skip-tour-btn"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}

      <GuidedTour
        run={runTour}
        onFinish={() => {
          setRunTour(false);
          localStorage.setItem("tour_completed", "true");
        }}
      />
      {upgradeModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setUpgradeModal(null)}
          data-testid="upgrade-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="upgrade-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#E91E8C]/10 flex items-center justify-center">
                <Lock size={22} weight="fill" className="text-[#E91E8C]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-zinc-900">
                  {upgradeModal.feature_display} is locked
                </h3>
                <p className="text-sm text-zinc-500">
                  {upgradeModal.tier_display} unlocks this
                </p>
              </div>
            </div>
            <p className="text-sm text-zinc-600 mb-6">
              {upgradeModal.feature === "series_studio" && "Publish a weekly episodic series with cinematic B-roll. Perfect for building a recognizable brand rhythm."}
              {upgradeModal.feature === "long_form" && "Render 2-30 minute videos from a script or your own audio upload. Webinars, YouTube long-form, courses."}
              {upgradeModal.feature === "auto_agent" && "Fully autonomous posting — the platform researches trends, writes scripts, renders videos, and posts on your schedule."}
              {!["series_studio","long_form","auto_agent"].includes(upgradeModal.feature) && "Upgrade your plan to unlock this feature."}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => { setUpgradeModal(null); setActiveSection("account"); }}
                data-testid="upgrade-modal-cta"
                className="flex-1 bg-[#E91E8C] hover:bg-[#D01880] text-white font-semibold py-2.5 rounded-lg transition"
              >
                Upgrade to {(upgradeModal.tier_required || "").replace(/^./, (c) => c.toUpperCase())}
              </button>
              <button
                onClick={() => setUpgradeModal(null)}
                data-testid="upgrade-modal-close"
                className="px-4 py-2.5 text-zinc-600 hover:bg-zinc-100 rounded-lg font-medium"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Don't redirect during OAuth callback — AppRouter handles session_id
  if (window.location.hash?.includes("session_id=")) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#E91E8C] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#E91E8C] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#E91E8C] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (user && user.onboarded) return <Navigate to="/dashboard" replace />;
  return children;
}

function PlansPublicPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="plans-public-page">
      <header className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
        <button onClick={() => navigate("/")} className="flex items-center" data-testid="plans-home-link">
          <img src={LOGO_URL} alt="The Branding Bar" className="h-9 object-contain" />
        </button>
        <button
          onClick={() => navigate("/login")}
          data-testid="plans-signin-btn"
          className="px-4 py-2 text-sm font-semibold text-[#E91E8C] hover:bg-[#E91E8C]/10 rounded-lg"
        >
          Sign in
        </button>
      </header>
      <PlanComparison onBack={() => navigate("/dashboard")} />
    </div>
  );
}

function PricingPublicPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="pricing-public-page">
      <header className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
        <button onClick={() => navigate("/")} className="flex items-center" data-testid="pricing-home-link">
          <img src={LOGO_URL} alt="The Branding Bar" className="h-9 object-contain" />
        </button>
        <button
          onClick={() => navigate("/login")}
          data-testid="pricing-signin-btn"
          className="px-4 py-2 text-sm font-semibold text-[#E91E8C] hover:bg-[#E91E8C]/10 rounded-lg"
        >
          Sign in
        </button>
      </header>
      <PlanComparison onBack={() => navigate("/dashboard")} />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously before route rendering
  // Use window.location.hash (not React Router's location.hash) for reliability
  const isCallback = window.location.hash?.includes("session_id=") || location.hash?.includes("session_id=") || location.pathname === "/auth/callback";
  if (isCallback) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/pricing" element={<PricingPublicPage />} />
      <Route path="/plans" element={<PlansPublicPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<Navigate to="/dashboard" replace />} />
      <Route path="/go/:slug" element={<GoRedirect />} />
      <Route path="/webinar" element={<WebinarPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
